import { UserRoundCheck } from 'lucide-react';
import {
  textoAvisoAtribucion,
  type AccionSobreLink,
  type AgenteAtribuido,
} from '@lib/aviso-atribucion';

export type { AccionSobreLink, AgenteAtribuido } from '@lib/aviso-atribucion';

/**
 * Aviso de atribución: cualquier acción sobre el link de la oferta MS2 (copiar,
 * enviar o reenviar por mail, generar/regenerar el link de pago) deja la venta
 * atribuida a quien la hace, aunque el link lo haya generado otro agente.
 *
 * Existe por el caso ETCHEVERRY (2026-09-14): el socio se presentó en persona,
 * la agente le mandó el link vigente que había creado otra compañera y la venta
 * quedó atribuida a la compañera. Ahora el server reatribuye al último agente
 * que entrega el link, y este cartel se lo dice ANTES de hacerlo, para que
 * nadie se lleve una sorpresa en ninguna de las dos direcciones. El texto vive
 * en `@lib/aviso-atribucion` (sin JSX).
 */

/** Versión inline, para meter dentro de una confirmación que ya existe. */
export function AvisoAtribucionTexto({
  accion,
  atribuidoA,
  yoEmail,
}: {
  accion: AccionSobreLink;
  atribuidoA?: AgenteAtribuido | null;
  yoEmail?: string | null;
}) {
  const { principal, detalle } = textoAvisoAtribucion(accion, atribuidoA, yoEmail);
  return (
    <p
      role="note"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        margin: 0,
        padding: '10px 12px',
        borderRadius: 8,
        background: 'rgba(0,37,100,0.06)',
        border: '1px solid rgba(0,37,100,0.14)',
        fontSize: 13,
        lineHeight: 1.5,
        color: '#002564',
      }}
    >
      <UserRoundCheck size={16} style={{ marginTop: 2, flexShrink: 0 }} />
      <span>
        <strong>{principal}</strong>
        {detalle && <> {detalle}</>}
      </span>
    </p>
  );
}

const LABEL_CONTINUAR: Record<AccionSobreLink, string> = {
  link: 'Entendido, copiar link',
  mail: 'Entendido, enviar mail',
  pago: 'Entendido, generar link de pago',
};

/**
 * Modal de confirmación para las acciones que NO tenían una ("Copiar link",
 * "Generar/Regenerar link de pago"). Las que ya tenían confirmación embeben
 * `AvisoAtribucionTexto` en la suya.
 */
export function AvisoAtribucionModal({
  accion,
  atribuidoA,
  yoEmail,
  enviando,
  onCancelar,
  onContinuar,
}: {
  accion: AccionSobreLink;
  atribuidoA?: AgenteAtribuido | null;
  yoEmail?: string | null;
  enviando?: boolean;
  onCancelar: () => void;
  onContinuar: () => void;
}) {
  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true" aria-labelledby="avisoAtribucionTitulo">
      <div className="modalCard" style={{ maxWidth: 460 }}>
        <h3 id="avisoAtribucionTitulo" className="heading-5" style={{ margin: '0 0 12px' }}>
          Atribución de la venta
        </h3>
        <AvisoAtribucionTexto accion={accion} atribuidoA={atribuidoA} yoEmail={yoEmail} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancelar} disabled={enviando}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={onContinuar} disabled={enviando}>
            {enviando ? 'Generando…' : LABEL_CONTINUAR[accion]}
          </button>
        </div>
      </div>
    </div>
  );
}
